import { useEffect, useState } from "react";
import {
  Mail,
  Users,
  Download,
  Search,
  Filter,
  CheckCircle2,
  XCircle,
  Calendar,
  Sparkles,
  Send,
  Radio,
  FileSpreadsheet
} from "lucide-react";
import { LoadingState, ErrorState } from "../../components/LoadingState";
import { EmptyState } from "../../components/common/EmptyState";
import { apiGet } from "../../services/api";
import { formatDate } from "../../utils/date";

export type Subscriber = {
  id: number;
  email: string;
  name: string | null;
  status: "subscribed" | "unsubscribed";
  source: string;
  consent_at: string;
  created_at: string;
};

export function NewsletterPage() {
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "subscribed" | "unsubscribed">("all");

  async function loadSubscribers() {
    try {
      setLoading(true);
      setError(null);
      const res = await apiGet<Subscriber[]>("/api/newsletter/subscribers");
      setSubscribers(res || []);
    } catch (err: any) {
      setError(err.message || "Failed to load newsletter subscribers");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadSubscribers();
  }, []);

  function handleExportCsv() {
    if (subscribers.length === 0) return;
    const headers = ["ID", "Email", "Name", "Status", "Source", "Subscribed Date"];
    const rows = filteredSubscribers.map((s) => [
      s.id,
      `"${s.email}"`,
      `"${s.name || ""}"`,
      s.status,
      s.source,
      `"${formatDate(s.created_at)}"`
    ]);
    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `zimsmart_subscribers_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const filteredSubscribers = subscribers.filter((s) => {
    const matchesStatus = statusFilter === "all" || s.status === statusFilter;
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      !q ||
      s.email.toLowerCase().includes(q) ||
      (s.name && s.name.toLowerCase().includes(q)) ||
      (s.source && s.source.toLowerCase().includes(q));
    return matchesStatus && matchesSearch;
  });

  const activeCount = subscribers.filter((s) => s.status === "subscribed").length;
  const unsubscribedCount = subscribers.filter((s) => s.status === "unsubscribed").length;

  return (
    <div className="admin-page-flow">
      {/* Header */}
      <header className="admin-section-header">
        <div>
          <div className="admin-breadcrumb-tag">
            <Mail size={13} className="text-[#C99A2E]" />
            <span>DISPATCH & AUDIENCE</span>
          </div>
          <h1 className="admin-title">Newsletter Subscribers</h1>
          <p className="admin-subtitle">
            Manage your publication’s email readership, subscriber opt-ins, and audience delivery lists.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={filteredSubscribers.length === 0}
            className="admin-btn-secondary"
          >
            <Download size={15} />
            <span>Export CSV</span>
          </button>
        </div>
      </header>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="admin-metric-card">
          <div className="flex items-center justify-between">
            <span className="admin-metric-label">Active Subscribers</span>
            <Users size={18} className="text-emerald-600" />
          </div>
          <div className="admin-metric-value">{activeCount}</div>
          <p className="admin-metric-note">Deliverable audience</p>
        </div>

        <div className="admin-metric-card">
          <div className="flex items-center justify-between">
            <span className="admin-metric-label">Unsubscribed</span>
            <XCircle size={18} className="text-slate-400" />
          </div>
          <div className="admin-metric-value">{unsubscribedCount}</div>
          <p className="admin-metric-note">Opted out from dispatches</p>
        </div>

        <div className="admin-metric-card">
          <div className="flex items-center justify-between">
            <span className="admin-metric-label">Total Opt-ins</span>
            <CheckCircle2 size={18} className="text-[#C99A2E]" />
          </div>
          <div className="admin-metric-value">{subscribers.length}</div>
          <p className="admin-metric-note">Lifetime registered readers</p>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-lg">
          {(["all", "subscribed", "unsubscribed"] as const).map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => setStatusFilter(status)}
              className={`px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-md transition-all ${
                statusFilter === status
                  ? "bg-white text-slate-900 shadow-xs"
                  : "text-slate-500 hover:text-slate-900"
              }`}
            >
              {status}
            </button>
          ))}
        </div>

        <div className="relative w-full sm:w-72">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search email, name, or source..."
            className="w-full pl-8 pr-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-[#C99A2E]"
          />
        </div>
      </div>

      {/* Main Table */}
      {loading ? (
        <LoadingState label="Loading subscriber registry..." />
      ) : error ? (
        <ErrorState message={error} />
      ) : filteredSubscribers.length === 0 ? (
        <div className="admin-card-panel p-0 overflow-hidden">
          <EmptyState
            icon={Mail}
            badge="NEWSLETTER"
            title="No subscribers found"
            description={searchQuery ? `No subscribers matched "${searchQuery}". Try a different filter.` : "No readers have subscribed to email dispatches yet."}
            actionText="Export CSV"
            onAction={handleExportCsv}
            actionIcon={Download}
          />
        </div>
      ) : (
        <div className="admin-card-panel p-0 overflow-hidden">
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Subscriber Email</th>
                  <th>Reader Name</th>
                  <th>Status</th>
                  <th>Acquisition Source</th>
                  <th>Subscribed Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubscribers.map((subscriber) => (
                  <tr key={subscriber.id}>
                    <td>
                      <div className="flex items-center gap-2">
                        <Mail size={14} className="text-[#C99A2E] shrink-0" />
                        <span className="font-semibold text-slate-900 text-xs">
                          {subscriber.email}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className="text-xs text-slate-700 font-medium">
                        {subscriber.name || <span className="italic text-slate-400">Anonymous Reader</span>}
                      </span>
                    </td>
                    <td>
                      {subscriber.status === "subscribed" ? (
                        <span className="status-pill status-published">
                          <span className="status-dot" />
                          <span>Subscribed</span>
                        </span>
                      ) : (
                        <span className="status-pill status-archived">
                          <span>Unsubscribed</span>
                        </span>
                      )}
                    </td>
                    <td>
                      <span className="text-xs font-mono text-slate-600 bg-slate-100 px-2 py-0.5 rounded">
                        {subscriber.source || "website"}
                      </span>
                    </td>
                    <td>
                      <span className="text-xs text-slate-500 font-medium whitespace-nowrap">
                        {formatDate(subscriber.created_at)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
